import { forwardRef } from 'react';
import styled from 'styled-components';

import type { SelectHTMLAttributes } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'children'
> {
  /** Label displayed above the select */
  label?: string;
  /** Helper text displayed below the select */
  helperText?: string;
  /** Error message — replaces helperText and triggers error styling */
  error?: string;
  /** Whether the select stretches to full container width */
  fullWidth?: boolean;
  /** Options to render */
  options: SelectOption[];
  /** Optional placeholder shown as first disabled option */
  placeholder?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      helperText,
      error,
      fullWidth,
      options,
      placeholder,
      className,
      ...rest
    },
    ref,
  ) => {
    return (
      <SelectWrapper className={className} $fullWidth={fullWidth}>
        {label && <Label>{label}</Label>}
        <SelectContainer $hasError={!!error}>
          <StyledSelect ref={ref} {...rest}>
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </StyledSelect>
          <ChevronIcon viewBox="0 0 16 16" fill="none">
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </ChevronIcon>
        </SelectContainer>
        {(error || helperText) && (
          <HelperText $isError={!!error}>{error || helperText}</HelperText>
        )}
      </SelectWrapper>
    );
  },
);

Select.displayName = 'Select';

export default Select;

const SelectWrapper = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
`;

const Label = styled.label`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-sm);
  color: var(--text-secondary);
`;

const SelectContainer = styled.div<{ $hasError?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  background-color: var(--input-background);
  border: 1px solid
    ${props => (props.$hasError ? 'var(--destructive)' : 'var(--border)')};
  border-radius: var(--radius-input);
  transition: border-color var(--transition-fast);

  &:focus-within {
    border-color: ${props =>
      props.$hasError ? 'var(--destructive)' : 'var(--focus-border)'};
    box-shadow: 0 0 0 1px
      ${props => (props.$hasError ? 'var(--destructive)' : 'var(--focus-ring)')};
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-xl) var(--spacing-sm)
    var(--spacing-md);
  appearance: none;
  background: transparent;
  border: none;
  outline: none;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: var(--foreground);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  option {
    background-color: var(--popover);
    color: var(--foreground);
  }
`;

const ChevronIcon = styled.svg`
  position: absolute;
  right: var(--spacing-md);
  width: 16px;
  height: 16px;
  color: var(--text-muted);
  pointer-events: none;
`;

const HelperText = styled.span<{ $isError?: boolean }>`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-xs);
  color: ${props =>
    props.$isError ? 'var(--destructive)' : 'var(--text-muted)'};
`;
